import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  CheckCircle, 
  Cancel, 
  SwapHoriz,
  Delete,
  Add,
  Close
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { 
  CambiTurno, 
  TurnoCreato, 
  CreaRichiestaCambiTurnoRequest,
  AccettaRichiestaCambiTurnoRequest,
  ApprovaRifiutaRichiestaCambiTurnoRequest,
  StatoCambiTurno,
  AzioneCambiTurno
} from '../types/types';
import { cambiTurnoService, orariGeneratiService } from '../api/services';

interface CambiTurnoPopupProps {
  open: boolean;
  onClose: () => void;
  turniDipendente: TurnoCreato[];
  user: {
    nome: string;
  ruolo: 'Dipendente' | 'ResponsabileOrari' | 'Coordinatore' | 'Admin' | 'Linker' | 'Maker' | 'Referente';
    negozio?: string;
  };
  onRefresh: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cambio-turno-tabpanel-${index}`}
      aria-labelledby={`cambio-turno-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CambiTurnoPopup: React.FC<CambiTurnoPopupProps> = ({
  open,
  onClose,
  turniDipendente,
  user,
  onRefresh
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Stati per le richieste
  const [mieRichieste, setMieRichieste] = useState<CambiTurno[]>([]);
  const [richiesteAccettabili, setRichiesteAccettabili] = useState<CambiTurno[]>([]);
  const [richiestePerApprovazione, setRichiestePerApprovazione] = useState<CambiTurno[]>([]);
  
  // Stati per i turni futuri
  const [turniFuturi, setTurniFuturi] = useState<TurnoCreato[]>([]);
  
  // Stati per creare nuova richiesta
  const [selectedTurno, setSelectedTurno] = useState<number | null>(null);
  const [motivazione, setMotivazione] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mieRichiesteData, richiesteAccettabiliData] = await Promise.all([
        cambiTurnoService.getMieRichieste(),
        cambiTurnoService.getRichiesteAccettabili()
      ]);
      
      setMieRichieste(mieRichiesteData);
      setRichiesteAccettabili(richiesteAccettabiliData);
      
      // Carica richieste per approvazione solo se coordinatore/admin
      if (user.ruolo === 'Coordinatore' || user.ruolo === 'Admin') {
        const richiestePerApprovazioneData = await cambiTurnoService.getRichiestePerApprovazione();
        setRichiestePerApprovazione(richiestePerApprovazioneData);
      }
    } catch (err) {
      setError('Errore nel caricamento dei dati');
      console.error('Errore caricamento dati:', err);
    } finally {
      setLoading(false);
    }
  }, [user.ruolo]);

  const loadTurniFuturi = useCallback(async () => {
    try {
      const oggi = new Date();
      const tra30giorni = new Date();
      tra30giorni.setDate(oggi.getDate() + 30);
      
      // Ottieni i turni creati per il dipendente nel periodo futuro
      const tuttiTurni = await orariGeneratiService.getByDipendente(
        user.nome,
        oggi.toISOString().split('T')[0],
        tra30giorni.toISOString().split('T')[0]
      );
      
      // Filtra solo i turni futuri (almeno 2 giorni da oggi) e non "ASSENZA"
      const dataLimite = new Date();
      dataLimite.setDate(oggi.getDate() + 2);
      
      const turniFiltrati = tuttiTurni.filter(turno => {
        const dataTurno = new Date(turno.data);
        return dataTurno >= dataLimite && turno.turno !== 'ASSENZA' && turno.turno !== 'Riposo';
      });
      
      setTurniFuturi(turniFiltrati);
    } catch (err) {
      console.error('Errore loading turni futuri:', err);
      setError('Errore nel caricamento dei turni futuri');
    }
  }, [user.nome]);

  useEffect(() => {
    if (open) {
      loadData();
      loadTurniFuturi();
      setSelectedTurno(null);
      setMotivazione('');
      setError(null);
      setSuccess(null);
    }
  }, [open, loadData, loadTurniFuturi]);

  const handleCreaRichiesta = async () => {
    if (!selectedTurno || !motivazione.trim()) {
      setError('Seleziona un turno e inserisci una motivazione');
      return;
    }

    setLoading(true);
    try {
      const request: CreaRichiestaCambiTurnoRequest = {
        idTurno: selectedTurno,
        motivazione: motivazione.trim()
      };

      await cambiTurnoService.creaRichiesta(request);
      setSuccess('Richiesta di cambio turno creata con successo');
      setSelectedTurno(null);
      setMotivazione('');
      await loadData();
      await loadTurniFuturi();
      onRefresh();
    } catch (err) {
      setError('Errore nella creazione della richiesta');
      console.error('Errore creazione richiesta:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAccettaRichiesta = async (richiesta: CambiTurno) => {
    // Trova il turno del dipendente per la stessa data
    const turnoCompatibile = turniDipendente.find(t => 
      t.data === richiesta.turnoRichiedente.data && 
      t.turno !== richiesta.turnoRichiedente.turno &&
      t.turno !== 'ASSENZA'
    );

    if (!turnoCompatibile) {
      setError('Non hai un turno compatibile per questa data');
      return;
    }

    setLoading(true);
    try {
      const request: AccettaRichiestaCambiTurnoRequest = {
        idCambiTurno: richiesta.idCambiTurno,
        idTurnoAccettante: turnoCompatibile.id
      };

      await cambiTurnoService.accettaRichiesta(request);
      setSuccess('Richiesta accettata con successo');
      await loadData();
      await loadTurniFuturi();
      onRefresh();
    } catch (err) {
      setError('Errore nell\'accettazione della richiesta');
      console.error('Errore accettazione richiesta:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnnullaRichiesta = async (idCambiTurno: number) => {
    setLoading(true);
    try {
      await cambiTurnoService.annullaRichiesta(idCambiTurno);
      setSuccess('Richiesta annullata con successo');
      await loadData();
      await loadTurniFuturi();
      onRefresh();
    } catch (err) {
      setError('Errore nell\'annullamento della richiesta');
      console.error('Errore annullamento richiesta:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovaRifiuta = async (idCambiTurno: number, azione: AzioneCambiTurno) => {
    setLoading(true);
    try {
      const request: ApprovaRifiutaRichiestaCambiTurnoRequest = {
        idCambiTurno,
        azione
      };

      await cambiTurnoService.approvaORifiutaRichiesta(request);
      setSuccess(`Richiesta ${azione === 'APPROVA' ? 'approvata' : 'rifiutata'} con successo`);
      await loadData();
      await loadTurniFuturi();
      onRefresh();
    } catch (err) {
      setError(`Errore nell'${azione === 'APPROVA' ? 'approvazione' : 'rifiuto'} della richiesta`);
      console.error('Errore approvazione/rifiuto:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatoChip = (stato: StatoCambiTurno) => {
    const colors = {
      'IN_ATTESA': 'warning',
      'IN_REVISIONE': 'info',
      'ACCETTATA': 'success',
      'RIFIUTATA': 'error',
      'ANNULLATA': 'default'
    } as const;

    return (
      <Chip
        label={stato.replace('_', ' ')}
        color={colors[stato]}
        size="small"
      />
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy', { locale: it });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  // Filtra i turni per creare richiesta (solo futuri e non già richiesti)
  const turniFiltrati = turniFuturi.filter(t => {
    return !mieRichieste.some(r => r.turnoRichiedente.id === t.id && 
                                   ['IN_ATTESA', 'IN_REVISIONE'].includes(r.stato));
  });

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          margin: fullScreen ? 0 : 'auto',
          maxHeight: fullScreen ? '100vh' : '90vh',
          height: fullScreen ? '100vh' : 'auto'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SwapHoriz color="primary" />
            <Typography variant="h6">Gestione Cambi Turno</Typography>
          </Box>
          {fullScreen && (
            <IconButton onClick={onClose} edge="end">
              <Close />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {/* Layout mobile-friendly con bottoni verticali invece dei tab orizzontali */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1, 
          mb: 2,
          '@media (min-width: 768px)': {
            flexDirection: 'row',
            borderBottom: 1, 
            borderColor: 'divider'
          }
        }}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            '@media (min-width: 768px)': {
              flexDirection: 'row',
              flex: 1
            }
          }}>
            <Button
              variant={tabValue === 0 ? "contained" : "outlined"}
              onClick={() => handleTabChange({} as React.SyntheticEvent, 0)}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start',
                '@media (min-width: 768px)': { 
                  minHeight: 48,
                  textTransform: 'none'
                }
              }}
            >
              Apri Richiesta
            </Button>
            <Button
              variant={tabValue === 1 ? "contained" : "outlined"}
              onClick={() => handleTabChange({} as React.SyntheticEvent, 1)}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start',
                '@media (min-width: 768px)': { 
                  minHeight: 48,
                  textTransform: 'none'
                }
              }}
            >
              Stato Richieste
            </Button>
            <Button
              variant={tabValue === 2 ? "contained" : "outlined"}
              onClick={() => handleTabChange({} as React.SyntheticEvent, 2)}
              fullWidth
              sx={{ 
                justifyContent: 'flex-start',
                '@media (min-width: 768px)': { 
                  minHeight: 48,
                  textTransform: 'none'
                }
              }}
            >
              Richieste del Team
            </Button>
            {(user.ruolo === 'Coordinatore' || user.ruolo === 'Admin') && (
              <Button
                variant={tabValue === 3 ? "contained" : "outlined"}
                onClick={() => handleTabChange({} as React.SyntheticEvent, 3)}
                fullWidth
                sx={{ 
                  justifyContent: 'flex-start',
                  '@media (min-width: 768px)': { 
                    minHeight: 48,
                    textTransform: 'none'
                  }
                }}
              >
                Gestione Approvazioni
              </Button>
            )}
          </Box>
        </Box>

        {/* Tab 1: Apri Richiesta */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>Crea Nuova Richiesta di Cambio Turno</Typography>
          
          {turniFiltrati.length === 0 ? (
            <Alert severity="info">
              Non hai turni disponibili per richiedere un cambio. I turni devono essere programmati almeno 2 giorni in anticipo.
            </Alert>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Seleziona Turno Da Cambiare</InputLabel>
                <Select
                  value={selectedTurno || ''}
                  onChange={(e) => setSelectedTurno(Number(e.target.value))}
                  label="Seleziona Turno Da Cambiare"
                >
                  {turniFiltrati.map((turno) => (
                    <MenuItem key={turno.id} value={turno.id}>
                      {`${formatDate(turno.data)} - ${turno.turno} (${formatTime(turno.oraPrevInizio)} - ${formatTime(turno.oraPrevFine)})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Motivazione"
                value={motivazione}
                onChange={(e) => setMotivazione(e.target.value)}
                placeholder="Inserisci la motivazione per il cambio turno..."
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                onClick={handleCreaRichiesta}
                disabled={loading || !selectedTurno || !motivazione.trim()}
                startIcon={<Add />}
                fullWidth
              >
                Crea Richiesta
              </Button>
            </>
          )}
        </TabPanel>

        {/* Tab 2: Stato Richieste */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>Le Mie Richieste</Typography>
          
          {/* Layout mobile-friendly con card invece di tabella */}
          <Box sx={{ 
            display: { xs: 'block', md: 'none' }, // Solo su mobile
          }}>
            {mieRichieste.length === 0 ? (
              <Alert severity="info">
                Nessuna richiesta presente
              </Alert>
            ) : (
              <Stack spacing={2}>
                {mieRichieste.map((richiesta) => (
                  <Card key={richiesta.idCambiTurno} variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {richiesta.turnoRichiedente.turno}
                        </Typography>
                        {getStatoChip(richiesta.stato)}
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Richiesta del: {formatDate(richiesta.dataCreazioneRichiesta)}
                      </Typography>
                      
                      <Typography variant="body2" gutterBottom>
                        <strong>Data Turno:</strong> {formatDate(richiesta.turnoRichiedente.data)}
                      </Typography>
                      
                      <Typography variant="body2" gutterBottom>
                        <strong>Orario:</strong> {formatTime(richiesta.turnoRichiedente.oraPrevInizio)} - {formatTime(richiesta.turnoRichiedente.oraPrevFine)}
                      </Typography>
                      
                      <Typography variant="body2" gutterBottom>
                        <strong>Accettante:</strong> {richiesta.turnoAccettante?.descrizione || 'In attesa'}
                      </Typography>
                      
                      {richiesta.stato === 'IN_ATTESA' && (
                        <Box mt={2}>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleAnnullaRichiesta(richiesta.idCambiTurno)}
                            disabled={loading}
                            startIcon={<Delete />}
                            fullWidth
                          >
                            Annulla Richiesta
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>

          {/* Layout desktop con tabella */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data Richiesta</TableCell>
                    <TableCell>Data Turno</TableCell>
                    <TableCell>Turno</TableCell>
                    <TableCell>Orario</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Accettante</TableCell>
                    <TableCell>Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mieRichieste.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary">Nessuna richiesta presente</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    mieRichieste.map((richiesta) => (
                      <TableRow key={richiesta.idCambiTurno}>
                        <TableCell>{formatDate(richiesta.dataCreazioneRichiesta)}</TableCell>
                        <TableCell>{formatDate(richiesta.turnoRichiedente.data)}</TableCell>
                        <TableCell>{richiesta.turnoRichiedente.turno}</TableCell>
                        <TableCell>
                          {formatTime(richiesta.turnoRichiedente.oraPrevInizio)} - {formatTime(richiesta.turnoRichiedente.oraPrevFine)}
                        </TableCell>
                        <TableCell>{getStatoChip(richiesta.stato)}</TableCell>
                        <TableCell>{richiesta.turnoAccettante?.descrizione || '-'}</TableCell>
                        <TableCell>
                          {richiesta.stato === 'IN_ATTESA' && (
                            <Tooltip title="Annulla richiesta">
                              <IconButton
                                color="error"
                                onClick={() => handleAnnullaRichiesta(richiesta.idCambiTurno)}
                                disabled={loading}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 3: Richieste del Team */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>Richieste che Puoi Accettare</Typography>
          
          {/* Layout mobile-friendly con card */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {richiesteAccettabili.length === 0 ? (
              <Alert severity="info">
                Nessuna richiesta disponibile
              </Alert>
            ) : (
              <Stack spacing={2}>
                {richiesteAccettabili.map((richiesta) => (
                  <Card key={richiesta.idCambiTurno} variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {richiesta.turnoRichiedente.descrizione}
                        </Typography>
                        <Chip label={richiesta.turnoRichiedente.turno} size="small" color="primary" />
                      </Box>
                      
                      <Typography variant="body2" gutterBottom>
                        <strong>Data Turno:</strong> {formatDate(richiesta.turnoRichiedente.data)}
                      </Typography>
                      
                      <Typography variant="body2" gutterBottom>
                        <strong>Orario:</strong> {formatTime(richiesta.turnoRichiedente.oraPrevInizio)} - {formatTime(richiesta.turnoRichiedente.oraPrevFine)}
                      </Typography>
                      
                      <Typography variant="body2" gutterBottom>
                        <strong>Motivazione:</strong> {richiesta.motivazione}
                      </Typography>
                      
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleAccettaRichiesta(richiesta)}
                          disabled={loading}
                          startIcon={<CheckCircle />}
                          fullWidth
                        >
                          Accetta Richiesta
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>

          {/* Layout desktop con tabella */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Richiedente</TableCell>
                    <TableCell>Data Turno</TableCell>
                    <TableCell>Turno Richiesto</TableCell>
                    <TableCell>Orario</TableCell>
                    <TableCell>Motivazione</TableCell>
                    <TableCell>Azioni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {richiesteAccettabili.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="textSecondary">Nessuna richiesta disponibile</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    richiesteAccettabili.map((richiesta) => (
                      <TableRow key={richiesta.idCambiTurno}>
                        <TableCell>{richiesta.turnoRichiedente.descrizione}</TableCell>
                        <TableCell>{formatDate(richiesta.turnoRichiedente.data)}</TableCell>
                        <TableCell>{richiesta.turnoRichiedente.turno}</TableCell>
                        <TableCell>
                          {formatTime(richiesta.turnoRichiedente.oraPrevInizio)} - {formatTime(richiesta.turnoRichiedente.oraPrevFine)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={richiesta.motivazione}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {richiesta.motivazione}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Accetta richiesta">
                            <IconButton
                              color="success"
                              onClick={() => handleAccettaRichiesta(richiesta)}
                              disabled={loading}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Tab 4: Gestione Approvazioni (solo per Coordinatori/Admin) */}
        {(user.ruolo === 'Coordinatore' || user.ruolo === 'Admin') && (
          <TabPanel value={tabValue} index={3}>
            <Typography variant="h6" gutterBottom>Richieste da Approvare</Typography>
            
            {/* Layout mobile-friendly con card */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {richiestePerApprovazione.length === 0 ? (
                <Alert severity="info">
                  Nessuna richiesta da approvare
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {richiestePerApprovazione.map((richiesta) => (
                    <Card key={richiesta.idCambiTurno} variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Scambio: {richiesta.turnoRichiedente.turno} ↔ {richiesta.turnoAccettante?.turno}
                        </Typography>
                        
                        <Typography variant="body2" gutterBottom>
                          <strong>Richiedente:</strong> {richiesta.turnoRichiedente.descrizione}
                        </Typography>
                        
                        <Typography variant="body2" gutterBottom>
                          <strong>Accettante:</strong> {richiesta.turnoAccettante?.descrizione || '-'}
                        </Typography>
                        
                        <Typography variant="body2" gutterBottom>
                          <strong>Data Turno:</strong> {formatDate(richiesta.turnoRichiedente.data)}
                        </Typography>
                        
                        <Typography variant="body2" gutterBottom>
                          <strong>Motivazione:</strong> {richiesta.motivazione}
                        </Typography>
                        
                        <Box mt={2}>
                          {richiesta.stato === 'IN_REVISIONE' ? (
                            <Box display="flex" gap={1}>
                              <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleApprovaRifiuta(richiesta.idCambiTurno, 'APPROVA')}
                                disabled={loading}
                                startIcon={<CheckCircle />}
                                fullWidth
                              >
                                Approva
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => handleApprovaRifiuta(richiesta.idCambiTurno, 'RIFIUTA')}
                                disabled={loading}
                                startIcon={<Cancel />}
                                fullWidth
                              >
                                Rifiuta
                              </Button>
                            </Box>
                          ) : richiesta.stato === 'IN_ATTESA' ? (
                            <Alert severity="info" sx={{ textAlign: 'center' }}>
                              In attesa di accettazione
                            </Alert>
                          ) : (
                            <Alert severity="success" sx={{ textAlign: 'center' }}>
                              Già gestita
                            </Alert>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Layout desktop con tabella */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Richiedente</TableCell>
                      <TableCell>Accettante</TableCell>
                      <TableCell>Data Turno</TableCell>
                      <TableCell>Scambio</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell>Motivazione</TableCell>
                      <TableCell>Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {richiestePerApprovazione.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="textSecondary">Nessuna richiesta da approvare</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      richiestePerApprovazione.map((richiesta) => (
                        <TableRow key={richiesta.idCambiTurno}>
                          <TableCell>{richiesta.turnoRichiedente.descrizione}</TableCell>
                          <TableCell>{richiesta.turnoAccettante?.descrizione || '-'}</TableCell>
                          <TableCell>{formatDate(richiesta.turnoRichiedente.data)}</TableCell>
                          <TableCell>
                            {richiesta.turnoRichiedente.turno} ↔ {richiesta.turnoAccettante?.turno}
                          </TableCell>
                          <TableCell>{getStatoChip(richiesta.stato)}</TableCell>
                          <TableCell>
                            <Tooltip title={richiesta.motivazione}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                {richiesta.motivazione}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {richiesta.stato === 'IN_REVISIONE' ? (
                              <Box display="flex" gap={1}>
                                <Tooltip title="Approva">
                                  <IconButton
                                    color="success"
                                    onClick={() => handleApprovaRifiuta(richiesta.idCambiTurno, 'APPROVA')}
                                    disabled={loading}
                                  >
                                    <CheckCircle />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Rifiuta">
                                  <IconButton
                                    color="error"
                                    onClick={() => handleApprovaRifiuta(richiesta.idCambiTurno, 'RIFIUTA')}
                                    disabled={loading}
                                  >
                                    <Cancel />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : richiesta.stato === 'IN_ATTESA' ? (
                              <Typography variant="caption" color="textSecondary">
                                In attesa di accettazione
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="textSecondary">
                                Già gestita
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        )}
      </DialogContent>

      {!fullScreen && (
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Chiudi
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CambiTurnoPopup;
